export function countWords(text) {
  const words = text.trim().match(/[A-Za-z0-9]+(?:[-'][A-Za-z0-9]+)*/g);
  return words ? words.length : 0;
}

export function getWords(text) {
  return String(text || "").match(/[A-Za-z0-9]+(?:[-'][A-Za-z0-9]+)*/g) || [];
}

export function normalizeWord(word) {
  return String(word || "")
    .toLowerCase()
    .replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, "")
    .replace(/'s$/, "")
    .replace(/ies$/, "y")
    .replace(/(sses|shes|ches|xes|zes)$/, (match) => match.slice(0, -2))
    .replace(/s$/, "")
    .trim();
}

export function countSentences(text) {
  const sentences = text.trim().split(/[.!?]+/).map((item) => item.trim()).filter(Boolean);
  return Math.max(sentences.length, text.trim() ? 1 : 0);
}

export function getSentences(text) {
  return String(text || "").match(/[^.!?]+[.!?]*/g)?.map((item) => item.trim()).filter(Boolean) || [];
}

export function meanSentenceLength(text) {
  const sentenceCount = countSentences(text);
  if (!sentenceCount) return 0;
  return countWords(text) / sentenceCount;
}

export function uniqueWordRatio(text) {
  const words = (text.toLowerCase().match(/[a-z0-9]+(?:[-'][a-z0-9]+)*/g) || []);
  if (!words.length) return 0;
  return new Set(words).size / words.length;
}

const FUNCTION_WORDS = new Set([
  "a", "about", "above", "after", "again", "against", "all", "am", "an", "and", "any", "are", "as", "at",
  "be", "because", "been", "before", "being", "below", "between", "both", "but", "by",
  "can", "could", "did", "do", "does", "doing", "down", "during",
  "each", "few", "for", "from", "further", "had", "has", "have", "having", "he", "her", "here", "hers", "him", "his", "how",
  "i", "if", "in", "into", "is", "it", "its", "itself",
  "just", "me", "more", "most", "my", "myself", "no", "nor", "not", "now", "of", "off", "on", "once", "only", "or", "other", "our", "out", "over", "own",
  "same", "she", "should", "so", "some", "such", "than", "that", "the", "their", "them", "then", "there", "these", "they", "this", "those", "through", "to", "too",
  "under", "until", "up", "very", "was", "we", "were", "what", "when", "where", "which", "while", "who", "why", "will", "with", "would", "you", "your"
]);

export function contentLemmas(text) {
  return getWords(text).map(normalizeWord).filter((word) => word && !FUNCTION_WORDS.has(word));
}

export function correctedTypeTokenRatio(text) {
  const words = contentLemmas(text);
  if (!words.length) return 0;
  return new Set(words).size / Math.sqrt(2 * words.length);
}

export function overusedContentWords(text, minimumUses = 3) {
  const counts = new Map();
  contentLemmas(text).forEach((word) => counts.set(word, (counts.get(word) || 0) + 1));
  return new Set([...counts.entries()].filter(([, count]) => count >= minimumUses).map(([word]) => word));
}

export function countUnitVocabulary(text, unitWords) {
  const normalizedText = ` ${getWords(text).map(normalizeWord).join(" ")} `;
  const keycounts = new Map();
  let count = 0;

  unitWords.forEach((unitWord) => {
    const normalizedUnitWord = getWords(unitWord).map(normalizeWord).filter(Boolean).join(" ");
    if (!normalizedUnitWord) return;
    const pattern = new RegExp(`\\b${normalizedUnitWord.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "g");
    const matches = normalizedText.match(pattern);
    const usage = matches ? matches.length : 0;
    keycounts.set(unitWord, usage);
    count += usage;
  });

  return { count, keycounts };
}

function getNgrams(words, size) {
  const grams = [];
  for (let index = 0; index <= words.length - size; index += 1) {
    grams.push(words.slice(index, index + size).join(" "));
  }
  return grams;
}

export function compareWritingToSource(size, writing, source) {
  const writingWords = getWords(writing).map(normalizeWord).filter(Boolean);
  const sourceWords = getWords(source).map(normalizeWord).filter(Boolean);
  const writingNgrams = getNgrams(writingWords, size);
  const sourceNgrams = new Set(getNgrams(sourceWords, size));
  const copiedWords = new Set();
  let hits = 0;

  writingNgrams.forEach((gram, index) => {
    if (!sourceNgrams.has(gram)) return;
    hits += 1;
    for (let offset = 0; offset < size; offset += 1) {
      copiedWords.add(index + offset);
    }
  });

  return {
    copyPCT: writingNgrams.length ? hits / writingNgrams.length : 0,
    copiedWords
  };
}
